import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Button, Divider, Input } from 'antd';

import Select from '@/components/ui/Select';
import { useActivity } from '../hooks/useActivity';
import { FormItemWrapper } from './FormItemWrapper';
import { useCreateActivity } from '@/hooks/useCreateActivity';
import { useApplicationContext } from '@/context/v1/Application/context';

interface ActivityFormItemProps {
	fieldName?: string | string[];
	required?: boolean;
}

export function ActivityFormItem({ required = true, fieldName }: Readonly<ActivityFormItemProps>): JSX.Element {
	const [activityName, setActivityName] = useState('');
	const {
		items,
		activityId,
		isFetching,
		isError,
		isDisabled,
		workstationId,
		handleSelectActivity,
		handleClearActivity
	} = useActivity({ fieldName });

	const { organization, company } = useApplicationContext();
	const { isLoading: isCreateActivityLoading, mutateAsync: createActivity } = useCreateActivity();

	async function handleCreateActivity(): Promise<void> {
		const name = activityName.trim();

		if (!name || !workstationId) return;

		const body = {
			organization_id: organization.id!,
			company_id: company.id!,
			workstation_id: workstationId,
			name
		};

		const created = await createActivity(body);

		const newId = created?.id;

		if (newId) {
			handleSelectActivity(newId);
		}

		setActivityName('');
	}

	return (
		<FormItemWrapper
			label={I18n.get('Activity')}
			name={fieldName}
			required={required}
			requiredMessage={I18n.get('The field requires an answer')}
		>
			<Select
				allowClear
				showSearch
				options={items}
				value={activityId}
				loading={isFetching}
				disabled={isFetching || isError || isDisabled}
				onClear={handleClearActivity}
				onChange={handleSelectActivity}
				placeholder={I18n.get('Activity')}
				dropdownRender={(menu) => (
					<>
						{menu}
						<Divider style={{ margin: '8px 0' }} />
						<div style={{ padding: '0 8px 4px', display: 'flex', gap: 8 }}>
							<Input
								value={activityName}
								onChange={(e) => setActivityName(e.target.value)}
								placeholder={I18n.get('New activity name')}
								style={{ flex: 1 }}
							/>
							<Button
								type="text"
								onClick={handleCreateActivity}
								loading={isCreateActivityLoading}
								disabled={!workstationId || !activityName.trim() || activityName.trim().length < 2}
							>
								{I18n.get('Add')}
							</Button>
						</div>
					</>
				)}
			/>
		</FormItemWrapper>
	);
}
